/**
 * orderStatus — safe formatter for `order.status` at render time.
 *
 * Feb 2026 (Sentry hotfix — ASH-AND-MERCH-FRONTEND-X/Y/Z crash class).
 *
 * BACKGROUND
 * ─────────────────────────────────────────────────────────────────────
 * Multiple production Sentry alerts fired with
 *   TypeError: undefined is not an object (evaluating 'e.status.replace')
 * from at least three separate customer/dispatch/driver surfaces. Every
 * one of them was rendering `order.status.replace('_', ' ')` directly.
 * Root cause: the SSE cache-bridge occasionally merges a partial order
 * shape into React Query cache (e.g. mid-flight `order.status_updated`
 * event landing before a full refetch), leaving `status` momentarily
 * `undefined`. The next paint tries to call `.replace()` on undefined
 * and the whole page ErrorBoundary trips.
 *
 * FIX
 * ─────────────────────────────────────────────────────────────────────
 * Coalesce missing/nullish status to `'pending'` at the single rendering
 * choke-point. That value matches the visual fallback that
 * `getStatusBadge()` and `getStatusIcon()` already use for unknown
 * statuses, so a broken order just looks pending for the split-second
 * before the full refetch heals it — never a crash.
 *
 * OBSERVABILITY
 * ─────────────────────────────────────────────────────────────────────
 * When we DO see a nullish status, we emit a low-noise Sentry warning
 * (level: 'warning', NOT 'error') so we can measure if the deeper SSE
 * race is still happening after the guard is in place — without any
 * user-facing crash forcing our hand. The message is deduped by Sentry
 * on the same fingerprint so this won't spam.
 */
import * as Sentry from '@sentry/react';

// Guard against Sentry double-reporting the same warning within a short
// window if a bad order re-renders on every SSE tick.
let _lastWarnAt = 0;

/**
 * Turn an order status into a human-readable display label ("out for
 * delivery", "preparing", "pending"). NEVER throws — coalesces nullish
 * or non-string values to 'pending'.
 *
 * @param {unknown} status  raw `order.status` value from the API/cache
 * @returns {string}        display-safe label
 */
export function formatOrderStatus(status) {
  if (status == null || typeof status !== 'string' || status === '') {
    // Fire-and-forget observability. Rate-limit to once per 5s so a
    // repeating render loop doesn't flood Sentry.
    try {
      const now = Date.now();
      if (now - _lastWarnAt > 5000) {
        _lastWarnAt = now;
        Sentry.captureMessage('order.status was null/undefined at render', {
          level: 'warning',
          tags: { area: 'order_render_guard' },
        });
      }
    } catch (_) { /* observability best-effort */ }
    return 'pending';
  }
  return status.replace(/_/g, ' ');
}
